import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} SwipeState
 * @property {number} startX - Touch start X coordinate
 * @property {number} startY - Touch start Y coordinate
 * @property {number} startTime - Timestamp of touch start
 * @property {number} deltaX - Current horizontal drag distance
 * @property {'h'|'v'|null} direction - Locked swipe axis (null = undetermined)
 * @property {boolean} active - Whether the swipe has produced visible movement
 */

/**
 * SD Media Gallery Component
 * Product image gallery with thumbnails, fullscreen, and touch swipe navigation
 */
class SDMediaGallery extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {SwipeState|null} */
  #swipe = null;

  /** @type {boolean} */
  #didSwipe = false;

  /** @type {Function|null} */
  #snapCleanup = null;

  constructor() {
    super();
    this.#queryElements();
  }

  connectedCallback() {
    this.#queryElements();
    this.setupThumbnails();
    this.setupArrows();
    this.setupArrowVisibilityObserver();
    this.setupFullscreen();
    this.#setupSwipe();
    this.#setupVideoPlayback();
    this.setupVariantUpdateListener();
  }

  /** Query and cache all gallery DOM elements */
  #queryElements() {
    this.thumbnails = this.querySelectorAll('.sd-media-gallery__thumbnail');
    this.slides = this.querySelectorAll('.sd-media-gallery__slide');
    this.slider = this.querySelector('.sd-media-gallery__slider');
    this.thumbnailsContainer = this.querySelector('.sd-media-gallery__thumbnails');
    this.prevButton = this.querySelector('.sd-media-gallery__arrow--prev');
    this.nextButton = this.querySelector('.sd-media-gallery__arrow--next');
    this.fullscreenDialog = this.querySelector('.sd-media-gallery__fullscreen');
    this.fullscreenSlides = this.querySelectorAll('.sd-media-gallery__fullscreen-slide');
    this.fullscreenThumbnails = this.querySelectorAll('.sd-media-gallery__fullscreen-thumbnail');
    this.fullscreenClose = this.querySelector('.sd-media-gallery__fullscreen-close');
    this.fullscreenPrev = this.querySelector('.sd-media-gallery__fullscreen-arrow--prev');
    this.fullscreenNext = this.querySelector('.sd-media-gallery__fullscreen-arrow--next');
    this.fullscreenMain = this.querySelector('.sd-media-gallery__fullscreen-main');
    this.statusRegion = this.querySelector('[data-gallery-status]');
  }

  /** Listen for variant updates to replace the gallery with updated content */
  setupVariantUpdateListener() {
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  /**
   * Handles a variant update event by replacing the gallery with the new one
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('sd-media-gallery');
    if (!newMediaGallery) return;

    // Pause any active video before replacing the gallery
    const activeSlide = this.querySelector('.sd-media-gallery__slide--active');
    this.#pauseSlideVideo(activeSlide);

    const activeElement = document.activeElement;
    const hadFocus = this.contains(activeElement);
    const focusedThumbnailIndex = hadFocus
      ? Array.from(this.thumbnails).indexOf(activeElement.closest('.sd-media-gallery__thumbnail'))
      : -1;

    this.replaceWith(newMediaGallery);

    if (focusedThumbnailIndex >= 0) {
      requestAnimationFrame(() => {
        const newThumbnails = newMediaGallery.querySelectorAll('.sd-media-gallery__thumbnail');
        newThumbnails[focusedThumbnailIndex]?.focus();
      });
    }
  };

  /** Watch for thumbnail container resize to update arrow visibility */
  setupArrowVisibilityObserver() {
    if (!this.thumbnailsContainer) return;
    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0) this.updateArrowVisibility();
      }
    });
    this.resizeObserver.observe(this.thumbnailsContainer);
  }

  disconnectedCallback() {
    this.#controller.abort();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /** Attach click listeners to all thumbnail buttons */
  setupThumbnails() {
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchMedia(thumbnail.dataset.mediaId);
      });
    });
  }

  /** Attach click and scroll listeners for thumbnail arrow navigation */
  setupArrows() {
    if (this.prevButton) this.prevButton.addEventListener('click', () => this.scrollThumbnails(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.scrollThumbnails(1));
    if (this.thumbnailsContainer) {
      this.thumbnailsContainer.addEventListener('scroll', () => this.updateArrowVisibility());
    }
  }

  /**
   * Switch the main gallery to a specific media item
   * @param {string} mediaId - The media ID to switch to
   */
  switchMedia(mediaId) {
    const currentSlide = Array.from(this.slides).find(
      (slide) => slide.classList.contains('sd-media-gallery__slide--active')
    );
    const targetSlide = Array.from(this.slides).find(
      (slide) => slide.dataset.slideId === mediaId
    );

    if (!targetSlide || targetSlide === currentSlide) return;

    this.#pauseSlideVideo(currentSlide);

    const currentIndex = Array.from(this.slides).indexOf(currentSlide);
    const targetIndex = Array.from(this.slides).indexOf(targetSlide);
    const isMovingForward = targetIndex > currentIndex;

    this.slides.forEach((slide) => slide.classList.remove('sd-media-gallery__slide--prev'));

    if (!isMovingForward) targetSlide.classList.add('sd-media-gallery__slide--prev');
    void targetSlide.offsetWidth;

    if (currentSlide) {
      currentSlide.classList.remove('sd-media-gallery__slide--active');
      if (isMovingForward) currentSlide.classList.add('sd-media-gallery__slide--prev');
    }
    targetSlide.classList.add('sd-media-gallery__slide--active');
    targetSlide.classList.remove('sd-media-gallery__slide--prev');

    this.thumbnails.forEach((thumb) => {
      const isActive = thumb.dataset.mediaId === mediaId;
      thumb.classList.toggle('sd-media-gallery__thumbnail--active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.fullscreenSlides.forEach((slide) => {
      slide.classList.toggle(
        'sd-media-gallery__fullscreen-slide--active',
        slide.dataset.fullscreenSlideId === mediaId
      );
    });
    this.fullscreenThumbnails.forEach((thumb) => {
      const isActive = thumb.dataset.fullscreenThumbId === mediaId;
      thumb.classList.toggle('sd-media-gallery__fullscreen-thumbnail--active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.#scrollThumbnailIntoView(mediaId);
    this.#announceSlideChange(mediaId);
    this.#autoplayIfVideo(targetSlide);
  }

  /**
   * Navigate main gallery by direction, with looping
   * @param {number} direction - Navigation direction (-1 = prev, 1 = next)
   */
  navigate(direction) {
    const slidesArray = Array.from(this.slides);
    const currentIndex = slidesArray.findIndex((s) =>
      s.classList.contains('sd-media-gallery__slide--active')
    );
    if (currentIndex === -1) return;

    const len = slidesArray.length;
    const newIndex = (currentIndex + direction + len) % len;
    const newSlide = slidesArray[newIndex];
    if (newSlide) this.switchMedia(newSlide.dataset.slideId);
  }

  /**
   * Update the aria-live status region with current slide position
   * @param {string} mediaId - The active media ID
   */
  #announceSlideChange(mediaId) {
    if (!this.statusRegion) return;
    const slidesArray = Array.from(this.slides);
    const index = slidesArray.findIndex((s) => s.dataset.slideId === mediaId);
    if (index === -1) return;
    this.statusRegion.textContent = `Image ${index + 1} of ${slidesArray.length}`;
  }

  /**
   * Scroll the thumbnails container to center the active thumbnail
   * @param {string} mediaId - The active media ID
   */
  #scrollThumbnailIntoView(mediaId) {
    if (!this.thumbnailsContainer) return;
    const thumb = this.thumbnailsContainer.querySelector(
      `.sd-media-gallery__thumbnail[data-media-id="${mediaId}"]`
    );
    if (!thumb) return;

    const containerRect = this.thumbnailsContainer.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const offset = thumbRect.left + thumbRect.width / 2 - (containerRect.left + containerRect.width / 2);

    if (Math.abs(offset) > 1) {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.thumbnailsContainer.scrollBy({ left: offset, behavior });
    }
  }

  /**
   * Scroll thumbnails by one step in the given direction
   * @param {number} direction - Scroll direction (-1 = left, 1 = right)
   */
  scrollThumbnails(direction) {
    if (!this.thumbnailsContainer) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.thumbnailsContainer.scrollBy({ left: direction * 116, behavior });
  }

  /** Update prev/next arrow opacity and disabled state based on scroll position */
  updateArrowVisibility() {
    if (!this.thumbnailsContainer || !this.prevButton || !this.nextButton) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.thumbnailsContainer;

    this.prevButton.style.opacity = scrollLeft > 0 ? '1' : '0.3';
    this.prevButton.disabled = scrollLeft <= 0;
    this.nextButton.style.opacity = scrollLeft < scrollWidth - clientWidth - 1 ? '1' : '0.3';
    this.nextButton.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
  }

  // ---------------------------------------------------------------------------
  // Fullscreen
  // ---------------------------------------------------------------------------

  /** Set up all fullscreen gallery event listeners */
  setupFullscreen() {
    if (!this.fullscreenDialog) return;

    // Click main slide to open fullscreen (suppressed after swipe or active video)
    this.slides.forEach((slide) => {
      slide.addEventListener('click', () => {
        if (this.#didSwipe) {
          this.#didSwipe = false;
          return;
        }
        if (slide.dataset.videoActive === 'true') return;
        this.openFullscreen();
      });
    });

    if (this.fullscreenClose) {
      this.fullscreenClose.addEventListener('click', () => this.closeFullscreen());
    }

    this.fullscreenDialog.addEventListener('click', (e) => {
      if (e.target === this.fullscreenDialog) this.closeFullscreen();
    });

    this.fullscreenDialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeFullscreen();
      else if (e.key === 'ArrowLeft') this.navigateFullscreen(-1);
      else if (e.key === 'ArrowRight') this.navigateFullscreen(1);
    });

    if (this.fullscreenPrev) {
      this.fullscreenPrev.addEventListener('click', () => this.navigateFullscreen(-1));
    }
    if (this.fullscreenNext) {
      this.fullscreenNext.addEventListener('click', () => this.navigateFullscreen(1));
    }

    this.fullscreenThumbnails.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.switchFullscreenSlide(thumb.dataset.fullscreenThumbId);
      });
    });
  }

  /** Open the fullscreen gallery dialog */
  openFullscreen() {
    if (!this.fullscreenDialog) return;
    this.fullscreenDialog.showModal();
    document.body.style.overflow = 'hidden';
  }

  /** Close the fullscreen gallery dialog */
  closeFullscreen() {
    if (!this.fullscreenDialog) return;

    const activeSlide = Array.from(this.fullscreenSlides).find((slide) =>
      slide.classList.contains('sd-media-gallery__fullscreen-slide--active')
    );
    this.#pauseSlideVideo(activeSlide);

    this.fullscreenDialog.close();
    document.body.style.overflow = '';
  }

  /**
   * Switch the fullscreen gallery to a specific media item and sync main gallery
   * @param {string} mediaId - The media ID to switch to
   */
  switchFullscreenSlide(mediaId) {
    const currentSlide = Array.from(this.fullscreenSlides).find((slide) =>
      slide.classList.contains('sd-media-gallery__fullscreen-slide--active')
    );
    const targetSlide = Array.from(this.fullscreenSlides).find(
      (slide) => slide.dataset.fullscreenSlideId === mediaId
    );

    if (!targetSlide || targetSlide === currentSlide) return;

    this.#pauseSlideVideo(currentSlide);

    this.fullscreenSlides.forEach((slide) => {
      slide.classList.remove('sd-media-gallery__fullscreen-slide--active');
    });
    targetSlide.classList.add('sd-media-gallery__fullscreen-slide--active');

    this.fullscreenThumbnails.forEach((thumb) => {
      const isActive = thumb.dataset.fullscreenThumbId === mediaId;
      thumb.classList.toggle('sd-media-gallery__fullscreen-thumbnail--active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.slides.forEach((slide) => {
      slide.classList.toggle('sd-media-gallery__slide--active', slide.dataset.slideId === mediaId);
    });
    this.thumbnails.forEach((thumb) => {
      const isActive = thumb.dataset.mediaId === mediaId;
      thumb.classList.toggle('sd-media-gallery__thumbnail--active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.#scrollThumbnailIntoView(mediaId);
    this.#announceSlideChange(mediaId);
    this.#autoplayIfVideo(targetSlide);
  }

  /**
   * Navigate fullscreen gallery by direction, with looping
   * @param {number} direction - Navigation direction (-1 = prev, 1 = next)
   */
  navigateFullscreen(direction) {
    const slides = Array.from(this.fullscreenSlides);
    const currentIndex = slides.findIndex((slide) =>
      slide.classList.contains('sd-media-gallery__fullscreen-slide--active')
    );
    if (currentIndex === -1) return;

    const newIndex = (currentIndex + direction + slides.length) % slides.length;
    const newSlide = slides[newIndex];
    if (newSlide) this.switchFullscreenSlide(newSlide.dataset.fullscreenSlideId);
  }

  // ---------------------------------------------------------------------------
  // Video Playback
  // ---------------------------------------------------------------------------

  /** @returns {boolean} Whether autoplay is enabled */
  get #autoplayVideo() {
    return this.hasAttribute('data-autoplay-video');
  }

  /** Set up video playback — play buttons and autoplay on active slide */
  #setupVideoPlayback() {
    // Attach click handlers to play buttons (manual play mode)
    this.querySelectorAll('.sd-media-gallery__play-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const slide = btn.closest(
          '.sd-media-gallery__slide, .sd-media-gallery__fullscreen-slide'
        );
        if (slide) this.#loadAndPlayVideo(slide);
      });
    });

    // Autoplay: load video on the currently active slide
    if (this.#autoplayVideo) {
      const activeSlide = this.querySelector('.sd-media-gallery__slide--active');
      if (activeSlide?.dataset.mediaType === 'video' || activeSlide?.dataset.mediaType === 'external_video') {
        this.#loadAndPlayVideo(activeSlide);
      }
    }
  }

  /**
   * Auto-load video if the target slide is a video and autoplay is on
   * @param {HTMLElement} slide - The slide that just became active
   */
  #autoplayIfVideo(slide) {
    if (!this.#autoplayVideo || !slide) return;
    const type = slide.dataset.mediaType;
    if (type === 'video' || type === 'external_video') {
      this.#loadAndPlayVideo(slide);
    }
  }

  /**
   * Load a video from the slide's template and start playback
   * @param {HTMLElement} slide - The slide containing the video template
   */
  #loadAndPlayVideo(slide) {
    // If video already loaded, resume playback
    if (slide.dataset.videoActive === 'true') {
      const video = slide.querySelector('video');
      if (video) video.play().catch(() => {});
      return;
    }

    const template = slide.querySelector('[data-video-template]');
    if (!template) return;

    const clone = template.content.cloneNode(true);
    const videoEl = clone.querySelector('video, iframe');
    if (!videoEl) return;

    // Hide preview image and play button
    const img = slide.querySelector('img');
    const playBtn = slide.querySelector('.sd-media-gallery__play-btn');
    if (img) img.style.display = 'none';
    if (playBtn) playBtn.style.display = 'none';

    slide.appendChild(videoEl);
    slide.dataset.videoActive = 'true';

    // Ensure playback starts for native video elements
    if (videoEl.tagName === 'VIDEO') {
      videoEl.play().catch(() => {});
    }

    template.remove();
  }

  /**
   * Pause any active video in a slide
   * @param {HTMLElement|null} slide - The slide to pause video in
   */
  #pauseSlideVideo(slide) {
    if (!slide || slide.dataset.videoActive !== 'true') return;

    const video = slide.querySelector('video');
    if (video) {
      video.pause();
      return;
    }

    const iframe = slide.querySelector('iframe');
    if (iframe) {
      // Pause YouTube
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
        '*'
      );
      // Pause Vimeo
      iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    }
  }

  // ---------------------------------------------------------------------------
  // Touch Swipe
  // ---------------------------------------------------------------------------

  /** Set up touch swipe handlers for main gallery and fullscreen */
  #setupSwipe() {
    if (this.slides.length <= 1) return;
    const { signal } = this.#controller;

    if (this.slider) {
      this.#attachSwipeListeners(this.slider, 'sd-media-gallery__slide--active', 'main', signal);
    }

    if (this.fullscreenMain && this.fullscreenSlides.length > 1) {
      this.#attachSwipeListeners(
        this.fullscreenMain,
        'sd-media-gallery__fullscreen-slide--active',
        'fullscreen',
        signal
      );
    }
  }

  /**
   * Attach touch event listeners for swipe navigation on a container
   * @param {HTMLElement} container - The swipeable container element
   * @param {string} activeClass - CSS class name for the active slide
   * @param {'main'|'fullscreen'} context - Which gallery context this serves
   * @param {AbortSignal} signal - AbortSignal for listener cleanup
   */
  #attachSwipeListeners(container, activeClass, context, signal) {
    const getSlides = () =>
      Array.from(context === 'fullscreen' ? this.fullscreenSlides : this.slides);

    container.addEventListener('touchstart', (e) => this.#onSwipeStart(e), {
      signal,
      passive: true,
    });

    container.addEventListener(
      'touchmove',
      (e) => this.#onSwipeMove(e, container, getSlides(), activeClass),
      { signal, passive: false }
    );

    container.addEventListener(
      'touchend',
      () => this.#onSwipeEnd(container, getSlides(), activeClass, context),
      { signal, passive: true }
    );

    container.addEventListener(
      'touchcancel',
      () => {
        if (this.#swipe?.active) this.#resetInlineStyles(getSlides());
        this.#swipe = null;
      },
      { signal, passive: true }
    );
  }

  /**
   * Handle touch start — record initial position and cancel any in-progress snap
   * @param {TouchEvent} e
   */
  #onSwipeStart(e) {
    // Cancel any in-progress snap animation
    if (this.#snapCleanup) {
      this.#snapCleanup();
      this.#snapCleanup = null;
    }

    const touch = e.touches[0];
    this.#swipe = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      deltaX: 0,
      direction: null,
      active: false,
    };
    this.#didSwipe = false;
  }

  /**
   * Handle touch move — lock axis, prevent scroll if horizontal, position slides
   * @param {TouchEvent} e
   * @param {HTMLElement} container - The swipeable container
   * @param {HTMLElement[]} slides - Array of slide elements
   * @param {string} activeClass - CSS class for the active slide
   */
  #onSwipeMove(e, container, slides, activeClass) {
    const state = this.#swipe;
    if (!state) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - state.startX;
    const deltaY = touch.clientY - state.startY;

    // Lock swipe axis on first significant movement
    if (state.direction === null) {
      if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
        state.direction = Math.abs(deltaX) >= Math.abs(deltaY) ? 'h' : 'v';
        if (state.direction === 'v') {
          this.#swipe = null;
          return;
        }
      } else {
        return;
      }
    }

    if (state.direction !== 'h') return;

    e.preventDefault();
    state.deltaX = deltaX;
    state.active = true;
    this.#didSwipe = true;

    // Position slides to follow finger
    const currentIndex = slides.findIndex((s) => s.classList.contains(activeClass));
    if (currentIndex === -1) return;

    const width = container.offsetWidth;
    const len = slides.length;
    const prevIndex = (currentIndex - 1 + len) % len;
    const nextIndex = (currentIndex + 1) % len;

    const applySwipeStyle = (slide, tx) => {
      slide.style.transform = `translateX(${tx}px)`;
      slide.style.opacity = '1';
      slide.style.visibility = 'visible';
      slide.style.transition = 'none';
      slide.style.willChange = 'transform';
    };

    applySwipeStyle(slides[currentIndex], deltaX);

    if (prevIndex === nextIndex && prevIndex !== currentIndex) {
      // 2-slide case: position adjacent on the side the user is dragging toward
      applySwipeStyle(slides[prevIndex], deltaX > 0 ? deltaX - width : deltaX + width);
    } else {
      if (prevIndex !== currentIndex) applySwipeStyle(slides[prevIndex], deltaX - width);
      if (nextIndex !== currentIndex) applySwipeStyle(slides[nextIndex], deltaX + width);
    }
  }

  /**
   * Handle touch end — determine navigation direction and trigger snap animation
   * @param {HTMLElement} container - The swipeable container
   * @param {HTMLElement[]} slides - Array of slide elements
   * @param {string} activeClass - CSS class for the active slide
   * @param {'main'|'fullscreen'} context - Gallery context
   */
  #onSwipeEnd(container, slides, activeClass, context) {
    const state = this.#swipe;
    if (!state?.active) {
      this.#swipe = null;
      return;
    }

    const { deltaX, startTime } = state;
    const elapsed = Date.now() - startTime;
    const velocity = Math.abs(deltaX) / Math.max(elapsed, 1);
    const width = container.offsetWidth;

    // Navigate if dragged >20% or fast flick (>0.4 px/ms)
    let navDirection = 0;
    if (Math.abs(deltaX) > width * 0.2 || velocity > 0.4) {
      navDirection = deltaX < 0 ? 1 : -1;
    }

    this.#snapSwipe(container, slides, activeClass, deltaX, navDirection, context);
    this.#swipe = null;
  }

  /**
   * Animate slides to their final positions after swipe release.
   * Respects prefers-reduced-motion by snapping instantly.
   * @param {HTMLElement} container - The swipeable container
   * @param {HTMLElement[]} slides - Array of slide elements
   * @param {string} activeClass - CSS class for the active slide
   * @param {number} currentDelta - Current drag offset in pixels
   * @param {number} navDirection - Navigation result (0 = snap back, 1 = next, -1 = prev)
   * @param {'main'|'fullscreen'} context - Gallery context
   */
  #snapSwipe(container, slides, activeClass, currentDelta, navDirection, context) {
    const currentIndex = slides.findIndex((s) => s.classList.contains(activeClass));
    if (currentIndex === -1) return;

    const width = container.offsetWidth;
    const len = slides.length;
    const prevIndex = (currentIndex - 1 + len) % len;
    const nextIndex = (currentIndex + 1) % len;
    const newIndex =
      navDirection === 0 ? currentIndex : navDirection === 1 ? nextIndex : prevIndex;

    // Respect prefers-reduced-motion: skip animation and snap instantly
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#finishSwipe(slides, activeClass, navDirection, newIndex, container, context);
      return;
    }

    // Target position for the current slide
    const targetX = navDirection === 0 ? 0 : navDirection === 1 ? -width : width;

    const snapTransition = 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)';

    // Animate current slide to target
    slides[currentIndex].style.transition = snapTransition;
    slides[currentIndex].style.transform = `translateX(${targetX}px)`;

    // Animate adjacent slides
    if (prevIndex === nextIndex && prevIndex !== currentIndex) {
      // 2-slide case
      const adj = slides[prevIndex];
      if (navDirection === 0) {
        // Snap back: return adjacent to the side it came from
        adj.style.transition = snapTransition;
        adj.style.transform = `translateX(${currentDelta > 0 ? -width : width}px)`;
      } else {
        // Navigate: adjacent becomes the new active at center
        adj.style.transition = snapTransition;
        adj.style.transform = 'translateX(0px)';
      }
    } else {
      if (prevIndex !== currentIndex) {
        slides[prevIndex].style.transition = snapTransition;
        slides[prevIndex].style.transform = `translateX(${targetX - width}px)`;
      }
      if (nextIndex !== currentIndex) {
        slides[nextIndex].style.transition = snapTransition;
        slides[nextIndex].style.transform = `translateX(${targetX + width}px)`;
      }
    }

    // After snap: update active state and clean up inline styles
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      this.#snapCleanup = null;
      this.#finishSwipe(slides, activeClass, navDirection, newIndex, container, context);
    };

    this.#snapCleanup = cleanup;
    slides[currentIndex].addEventListener('transitionend', cleanup, { once: true });
    // Fallback in case transitionend doesn't fire
    setTimeout(cleanup, 300);
  }

  /**
   * Finalize a swipe by updating active classes and removing all inline styles.
   * Called after snap animation completes or immediately for reduced motion.
   * @param {HTMLElement[]} slides - Array of slide elements
   * @param {string} activeClass - CSS class for the active slide
   * @param {number} navDirection - Navigation result (0 = snap back, 1 = next, -1 = prev)
   * @param {number} newIndex - Index of the new active slide
   * @param {HTMLElement} container - The swipeable container (for reflow)
   * @param {'main'|'fullscreen'} context - Gallery context
   */
  #finishSwipe(slides, activeClass, navDirection, newIndex, container, context) {
    slides.forEach((slide) => {
      slide.style.transition = 'none';
    });

    if (navDirection !== 0) {
      const newSlide = slides[newIndex];
      const mediaId =
        context === 'fullscreen'
          ? newSlide.dataset.fullscreenSlideId
          : newSlide.dataset.slideId;

      if (context === 'fullscreen') {
        this.switchFullscreenSlide(mediaId);
      } else {
        this.switchMedia(mediaId);
      }
    }

    slides.forEach((slide) => {
      slide.style.transform = '';
      slide.style.opacity = '';
      slide.style.visibility = '';
      slide.style.willChange = '';
    });

    void container.offsetWidth;
    slides.forEach((slide) => {
      slide.style.transition = '';
    });
  }

  /**
   * Immediately remove all swipe-related inline styles (used on touchcancel)
   * @param {HTMLElement[]} slides - Array of slide elements
   */
  #resetInlineStyles(slides) {
    slides.forEach((slide) => {
      slide.style.transition = 'none';
      slide.style.transform = '';
      slide.style.opacity = '';
      slide.style.visibility = '';
      slide.style.willChange = '';
    });
    requestAnimationFrame(() => {
      slides.forEach((slide) => {
        slide.style.transition = '';
      });
    });
  }
}

if (!customElements.get('sd-media-gallery')) {
  customElements.define('sd-media-gallery', SDMediaGallery);
}
